import {
    Rocket,
    Shield,
    Globe,
    Code2,
    LineChart,
    Lock,
    Zap,
    Cpu,
} from "lucide-react";

export const FEATURES = [
    {
        icon: Rocket,
        title: "Performance",
        shortDescription: "Lightning Fast Performance",
        description: "Optimized for speed with cutting-edge technology",
        slug: "performance",
        color: "text-blue-500",
        image: "/images/features/performance.jpg",
        details: {
            heading: "Supercharge Your Application Performance",
            subheading: "Experience blazing-fast speed and optimal efficiency",
            benefits: [
                "Advanced caching mechanisms",
                "Optimized database queries",
                "Automated performance monitoring",
                "Load balancing and scaling",
                "CDN integration"
            ],
            metrics: [
                { label: "Average Response Time", value: "<100ms" },
                { label: "Server Uptime", value: "99.99%" },
                { label: "Global CDN Points", value: "200+" }
            ],
            features: [
                "Real-time performance analytics",
                "Automatic optimization suggestions",
                "Custom performance rules",
                "Performance history tracking",
                "Alert system for performance drops"
            ]
        }
    },
    {
        icon: Shield,
        title: "Security",
        shortDescription: "Enterprise-Grade Security",
        description: "Advanced security measures and protocols",
        slug: "security",
        color: "text-green-500",
        image: "/images/features/security.jpg",
        details: {
            heading: "Bank-Grade Security for Your Applications",
            subheading: "Protect your data with military-grade encryption",
            benefits: [
                "End-to-end encryption",
                "Multi-factor authentication",
                "Regular security audits",
                "Automated threat detection",
                "Compliance monitoring"
            ],
            metrics: [
                { label: "Security Score", value: "A+" },
                { label: "Encryption Level", value: "256-bit" },
                { label: "Security Updates", value: "Daily" }
            ],
            features: [
                "Real-time threat monitoring",
                "Automated backup systems",
                "Access control management",
                "Security compliance tools",
                "Incident response system"
            ]
        }
    },
    {
        icon: Globe,
        title: "Global CDN",
        shortDescription: "Worldwide Content Delivery",
        description: "Fast access from anywhere in the world",
        slug: "global-cdn",
        color: "text-purple-500",
        image: "/images/features/global-cdn.jpg",
        details: {
            heading: "Lightning-Fast Global Content Delivery",
            subheading: "Serve content instantly anywhere in the world",
            benefits: [
                "Global server distribution",
                "Automatic route optimization",
                "Dynamic content caching",
                "DDoS protection",
                "SSL/TLS encryption"
            ],
            metrics: [
                { label: "Global Points of Presence", value: "250+" },
                { label: "Average Latency", value: "<50ms" },
                { label: "Network Coverage", value: "99.9%" }
            ],
            features: [
                "Smart routing technology",
                "Real-time analytics",
                "Custom cache rules",
                "Edge computing capabilities",
                "Bandwidth optimization"
            ]
        }
    },
    {
        icon: Code2,
        title: "Development",
        shortDescription: "Modern Development Tools",
        description: "Latest development tools and frameworks",
        slug: "development",
        color: "text-yellow-500",
        image: "/images/features/development.jpg",
        details: {
            heading: "Cutting-Edge Development Environment",
            subheading: "Build faster with modern tools and frameworks",
            benefits: [
                "Integrated development environment",
                "Version control system",
                "Automated testing",
                "CI/CD pipeline",
                "Code quality tools"
            ],
            metrics: [
                { label: "Development Speed", value: "2x Faster" },
                { label: "Code Quality Score", value: "95%" },
                { label: "Test Coverage", value: "100%" }
            ],
            features: [
                "Real-time collaboration",
                "Automated code review",
                "Integrated debugging tools",
                "Package management",
                "Development analytics"
            ]
        }
    },
    {
        icon: LineChart,
        title: "Analytics",
        shortDescription: "Advanced Analytics",
        description: "Comprehensive insights and tracking",
        slug: "analytics",
        color: "text-red-500",
        image: "/images/features/analytics.jpg",
        details: {
            heading: "Data-Driven Insights and Analytics",
            subheading: "Make informed decisions with powerful analytics",
            benefits: [
                "Real-time data tracking",
                "Custom report generation",
                "Predictive analytics",
                "User behavior analysis",
                "Performance metrics"
            ],
            metrics: [
                { label: "Data Processing", value: "Real-time" },
                { label: "Report Types", value: "50+" },
                { label: "Data Points", value: "1M+" }
            ],
            features: [
                "Interactive dashboards",
                "Custom metric tracking",
                "Automated reporting",
                "Data visualization tools",
                "Export capabilities"
            ]
        }
    },
    {
        icon: Lock,
        title: "Access Control",
        shortDescription: "Granular Access Management",
        description: "Precise control over user permissions",
        slug: "access-control",
        color: "text-indigo-500",
        image: "/images/features/access-control.jpg",
        details: {
            heading: "Advanced Access Control System",
            subheading: "Manage permissions with precision",
            benefits: [
                "Role-based access control",
                "Custom permission sets",
                "Access audit logs",
                "Single sign-on",
                "Multi-factor authentication"
            ],
            metrics: [
                { label: "Permission Levels", value: "Unlimited" },
                { label: "Access Rules", value: "Custom" },
                { label: "Security Score", value: "99%" }
            ],
            features: [
                "User role management",
                "Access policy creation",
                "Authentication methods",
                "Activity monitoring",
                "Compliance tools"
            ]
        }
    },
    {
        icon: Zap,
        title: "Real-time",
        shortDescription: "Real-time Updates",
        description: "Instant data synchronization",
        slug: "real-time",
        color: "text-pink-500",
        image: "/images/features/real-time.jpg",
        details: {
            heading: "Instant Real-Time Updates",
            subheading: "Stay synchronized across all platforms",
            benefits: [
                "Instant data sync",
                "Real-time collaboration",
                "Live notifications",
                "Automated updates",
                "Offline support"
            ],
            metrics: [
                { label: "Update Speed", value: "<10ms" },
                { label: "Sync Rate", value: "100%" },
                { label: "Reliability", value: "99.99%" }
            ],
            features: [
                "WebSocket integration",
                "Event streaming",
                "Conflict resolution",
                "State management",
                "Real-time analytics"
            ]
        }
    },
    {
        icon: Cpu,
        title: "AI Solutions",
        shortDescription: "AI-Powered Features",
        description: "Intelligent automation and insights",
        slug: "ai-solutions",
        color: "text-orange-500",
        image: "/images/features/ai-solutions.jpg",
        details: {
            heading: "Advanced AI-Powered Solutions",
            subheading: "Transform your workflow with artificial intelligence",
            benefits: [
                "Automated decision making",
                "Predictive analytics",
                "Natural language processing",
                "Machine learning models",
                "AI-driven insights"
            ],
            metrics: [
                { label: "AI Accuracy", value: "98%" },
                { label: "Processing Speed", value: "Real-time" },
                { label: "Model Types", value: "25+" }
            ],
            features: [
                "Custom AI models",
                "Automated learning",
                "Pattern recognition",
                "Predictive analysis",
                "AI optimization tools"
            ]
        }
    }
];